'use client';

import React, { useEffect, useState, useRef, useImperativeHandle, forwardRef } from 'react';
import {
  buildSearchBox,
  SearchBoxState,
  SearchEngine,
  SearchBox as HeadlessSearchBox,
} from '@coveo/headless';
import { useEngineContext } from 'lib/utils/coveo/SearchProvider';
import { useRouter, usePathname, useSearchParams } from 'next/navigation';
import PlainTextWrapper from 'helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import { Field } from '@sitecore-content-sdk/nextjs';
import { SvgIcon } from 'helpers/SvgIcon';
import useIsEditing from 'lib/hooks/useIsEditing';
import { useCoveoEngine } from 'lib/utils/coveo/SearchContext/CoveoEngineContext';

export interface SearchBoxProps {
  placeholder?: Field<string>;
  onSubmitRedirect?: boolean;
  redirectTo?: string;
  onClear?: () => void;
  onSubmit?: () => void;
  hasCoveoWrapper?: boolean;
}

export interface SearchBoxHandle {
  getCurrentValue: () => string;
  setCurrentValue: (value: string) => void;
  submit: () => void;
}

export const SearchBox = forwardRef<SearchBoxHandle, SearchBoxProps>(
  (
    {
      placeholder = { value: 'search' },
      onSubmitRedirect = true,
      redirectTo = '',
      onClear,
      onSubmit,
      hasCoveoWrapper = false,
    },
    ref
  ) => {
    const parentEngine = useCoveoEngine();
    const engineContext = useEngineContext();
    const engine = (hasCoveoWrapper ? parentEngine : engineContext) as SearchEngine;
    const router = useRouter();
    const pathname = usePathname();
    const searchParams = useSearchParams();

    const controllerRef = useRef<HeadlessSearchBox | null>(null);
    if (!controllerRef.current && engine) {
      controllerRef.current = buildSearchBox(engine, {
        options: {
          numberOfSuggestions: 10,
          highlightOptions: {
            notMatchDelimiters: {
              open: '<span class="font-normal">',
              close: '</span>',
            },
            correctionDelimiters: {
              open: '<i>',
              close: '</i>',
            },
          },
        },
      });
    }

    const [state, setState] = useState<SearchBoxState>(
      controllerRef.current
        ? controllerRef.current.state
        : ({ value: '', suggestions: [] } as unknown as SearchBoxState)
    );
    const inputRef = useRef<HTMLInputElement | null>(null);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const suggestionsRef = useRef<HTMLUListElement>(null);
    const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
    const [condition, setCondition] = useState('');
    const [isConditionFocused, setIsConditionFocused] = useState(false);
    const formRef = useRef<HTMLFormElement>(null);
    const isEditing = useIsEditing();

    useEffect(() => {
      if (!controllerRef.current) return;
      const unsubscribe = controllerRef.current.subscribe(() => {
        const newState = controllerRef.current!.state;
        setState(newState);
        setCondition(newState.value || '');
      });
      return () => unsubscribe();
    }, []);
    useImperativeHandle(ref, () => ({
      getCurrentValue: () => controllerRef.current?.state.value || '',
      setCurrentValue: (value: string) => {
        controllerRef.current?.updateText(value);
        setCondition(value);
        setIsConditionFocused(!!value);
      },
      submit: () => {
        controllerRef.current?.submit();
      },
    }));

    useEffect(() => {
      function handleClickOutside(event: MouseEvent) {
        if (
          suggestionsRef.current &&
          !suggestionsRef.current.contains(event.target as Node) &&
          inputRef.current &&
          event.target !== inputRef.current
        ) {
          setShowSuggestions(false);
        }
      }
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, []);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      controllerRef.current?.updateText(newValue);
      setShowSuggestions(true);
      setHighlightedIndex(-1);
      if (newValue.trim() === '') {
        setCondition('');
      } else {
        setCondition(newValue);
        setIsConditionFocused(true);
      }
    };

    const handleSubmit = (e?: React.FormEvent<HTMLFormElement>) => {
      if (e) e.preventDefault();

      if (typeof onSubmit === 'function') {
        onSubmit();
      } else {
        controllerRef.current?.submit();
        const query = controllerRef.current?.state.value.trim();
        if (query && onSubmitRedirect) {
          router.push(`${redirectTo}?q=${encodeURIComponent(query)}`);
        }
      }
      setShowSuggestions(false);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      const suggestions = state.suggestions || [];
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlightedIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : 0));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : suggestions.length - 1));
      } else if (e.key === 'Enter') {
        if (highlightedIndex >= 0) {
          e.preventDefault();
          const suggestion = suggestions[highlightedIndex];
          if (suggestion) {
            controllerRef.current?.updateText(suggestion.rawValue);
            setIsConditionFocused(true);
            setShowSuggestions(false);
            handleSubmit();
          }
        }
        // If highlightedIndex is -1, let native form submit handle it cleanly.
      } else if (e.key === 'Escape') {
        setShowSuggestions(false);
        setHighlightedIndex(-1);
      }
    };

    const handleSuggestionClick = (suggestion: string) => {
      controllerRef.current?.updateText(suggestion);
      setCondition(suggestion);
      setIsConditionFocused(true);
      setShowSuggestions(false);
      handleSubmit();
    };

    const handleFocus = (e: React.ChangeEvent<HTMLInputElement>) => {
      controllerRef.current?.showSuggestions();
      setShowSuggestions(true);
      setCondition(e.target.value);
      setIsConditionFocused(true);
    };

    const handleBlur = () => {
      if (!state.value) {
        setIsConditionFocused(false);
      } else {
        setIsConditionFocused(true);
      }
    };

    useEffect(() => {
      const formEl = formRef.current;
      const handleFocusOut = (event: FocusEvent) => {
        const relatedTarget = event.relatedTarget as Node | null;
        if (formEl && relatedTarget && !formEl.contains(relatedTarget)) {
          setShowSuggestions(false);
        }
      };

      formEl?.addEventListener('focusout', handleFocusOut, true);
      return () => {
        formEl?.removeEventListener('focusout', handleFocusOut, true);
      };
    }, []);

    // Fix 3: Cleaned clear button handler
    const handleClear = () => {
      setCondition('');
      setIsConditionFocused(false);
      controllerRef.current?.updateText('');
      controllerRef.current?.clear();
      setShowSuggestions(false);
      setHighlightedIndex(-1);
      inputRef.current?.blur();
      onClear?.();

      if (!hasCoveoWrapper && onSubmitRedirect) {
        let basePath = pathname;
        let params: URLSearchParams;
        if (redirectTo) {
          const url = new URL(redirectTo, window.location.origin);
          basePath = url.pathname;
          params = new URLSearchParams(url.search);
        } else {
          params = new URLSearchParams(searchParams.toString());
        }
        params.delete('q');
        const newUrl = `${basePath}${params.toString() ? `?${params.toString()}` : ''}`;
        router.replace(newUrl, { scroll: false });
      }
    };

    return (
      <form ref={formRef} onSubmit={handleSubmit} className="relative">
        <div className="flex-1 relative focus:outline-2 focus:outline-blue-base bg-white lg:rounded-l-sm lg:rounded-tr-none rounded-t-sm">
          <input
            ref={inputRef}
            type="text"
            className="w-full text-18 leading-28 text-gray-900 px-6 pt-[30px] pb-5 focus:outline-2 focus:outline-blue-base lg:rounded-l-sm lg:rounded-tr-none rounded-t-sm"
            value={state.value || ''}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            aria-label={state.value ? state.value : placeholder.value}
            autoComplete="off"
            id="conditionInput"
            onFocus={handleFocus}
            onBlur={handleBlur}
            readOnly={isEditing}
          />

          <label
            htmlFor="conditionInput"
            className={`absolute left-6 transition-all text-gray-900 duration-200 pointer-events-none overflow-hidden text-ellipsis max-w-[94%] whitespace-nowrap ${
              condition || isConditionFocused
                ? 'text-14 leading-16 top-[10px]'
                : 'text-18 leading-24 top-[20px]'
            }`}
          >
            <PlainTextWrapper field={placeholder} />
          </label>
          {condition && (
            <button
              type="button"
              className="absolute right-4 top-1/2 -translate-y-1/2 bg-gray-500 text-white rounded-full w-[24px] h-[24px] flex items-center justify-center"
              onClick={handleClear}
              aria-label="Clear input"
            >
              <SvgIcon icon="close" />
            </button>
          )}
          {showSuggestions && state.suggestions && state.suggestions.length > 0 && (
            <ul
              ref={suggestionsRef}
              className="dropdown-scroll max-h-[240px] absolute lg:top-[77px] top-[70px] h-auto bg-white left-0 w-full lg:border-2 border border-gray-100 rounded-sm overflow-auto py-2 z-10 shadow-default"
            >
              {state.suggestions.map((suggestion, index) => {
                const isHighlighted = index === highlightedIndex;
                return (
                  <li
                    key={suggestion.rawValue}
                    ref={(el) => {
                      if (isHighlighted && el) {
                        el.scrollIntoView({ block: 'nearest' });
                      }
                    }}
                    className={`px-8 py-5 hover:bg-blue-50 hover:underline hover:cursor-pointer font-bold ${
                      index === highlightedIndex ? 'bg-blue-50' : ''
                    }`}
                    onMouseDown={() => handleSuggestionClick(suggestion.rawValue)}
                    dangerouslySetInnerHTML={{ __html: suggestion.highlightedValue }}
                  ></li>
                );
              })}
            </ul>
          )}
        </div>
      </form>
    );
  }
);

SearchBox.displayName = 'SearchBox';
